import React, { FC, ReactNode } from 'react';
import { CurrencyToString } from '@wix/premium-react-text';
import { SummaryItem, CycleSelector } from '@wix/checkout-components';
import { SummaryProps } from '@wix/premium-purchase-checkout-form';
import { Cycle } from '@wix/ambassador-premium-store/types';
import { Text } from '@wix/design-system';
import { TFunction } from 'i18next';
import { CycleDetails, Product, SentryReporter } from '../../../../types';
import {
  calculateMonthlyPriceBefore,
  convertCycleSelectorToPaymentCycle,
  couponNamesToTranslationKeys,
  currentProductCycle,
  CycleMultiplier,
  PaymentCycle,
} from '../../../../utils';
import { useCheckoutStore } from '../../../../providers';
import { CheckoutRowLabel } from '../../../../components/Checkout/CheckoutRowLabel/CheckoutRowLabel';
import { CheckoutSummaryFooter } from '../../../../components/Checkout/CheckoutSummaryFooter/CheckoutSummaryFooter';
import { SummaryRowProps } from '@wix/premium-purchase-checkout-form/dist/src/components/Summary/SummaryRow';

const BASE_MULTIPLIER = 1;
const FALLBACK_DISCOUNT_PERCENTAGE = 50;

interface RenderItemsOptions {
  currentProduct: Product;
  currentCycle: PaymentCycle;
  isInSale: boolean;
  isCheckoutSavingsEnabled: boolean;
  isRemoveStrikePriceEnabled: boolean;
  isNoVatInCheckoutSummeryEnabled: boolean;
  SummaryRowComp: FC<SummaryRowProps>;
  setCurrentCycle: (options: {
    cycle: PaymentCycle;
    sendBiEvent?: boolean;
  }) => void;
  logError: SentryReporter;
}

interface CustomFooterOptions {
  isSecondUpgrade: boolean;
  isChangePlan: boolean;
}

interface SummaryRenderItemsOptions extends RenderItemsOptions {
  alternativeCycle: CycleSelector | null;
  currencyToString: CurrencyToString;
  tFunction: TFunction;
}

interface InjectedCouponDisplayNameOptions {
  currentProduct: Product;
  isInSale: boolean;
  isEditorXSpecialDiscountEnabled: boolean;
}

interface SummaryCouponLabelOptions extends InjectedCouponDisplayNameOptions {
  tFunction: TFunction;
}

interface ChangeCycleOptions {
  items: SummaryItem[];
  alternativeCycle: CycleSelector | null;
  currentCycle: PaymentCycle;
  logError: SentryReporter;
  setCurrentCycle: (options: {
    cycle: PaymentCycle;
    sendBiEvent?: boolean;
  }) => void;
}

export interface UseSummaryPropsOptions {
  tFunction: TFunction;
  currencyToString: CurrencyToString;
  renderItemsOptions: RenderItemsOptions;
  injectedCouponDisplayNameOptions: InjectedCouponDisplayNameOptions;
  renderCustomFooterOptions?: CustomFooterOptions;
  customDisclaimer?: ReactNode;
  creditExplanation?: ReactNode;
}

function changeCycleIfRequired({
  alternativeCycle,
  items,
  currentCycle,
  logError,
  setCurrentCycle,
}: ChangeCycleOptions) {
  try {
    if (alternativeCycle && items?.length > 0) {
      const alternativePaymentCycle = convertCycleSelectorToPaymentCycle(
        items[0].cycle as Cycle,
      );
      // cycle changed in checkout-form when user clicked on boleto "Switch to Yearly"
      currentCycle !== alternativePaymentCycle &&
        setCurrentCycle({
          cycle: alternativePaymentCycle,
          sendBiEvent: false,
        });
    }
  } catch {
    logError(
      new Error(
        'failed in changeCycleIfRequired, items: ' + JSON.stringify(items),
      ),
    );
  }
}

function getSummeryPrices(
  isNoVatInCheckoutSummeryEnabled: boolean,
  cycleDetails: CycleDetails,
  cycleMultiplier: number = BASE_MULTIPLIER,
): { cycleMonthlyPrice: number; cycleFullTermPrice: number } {
  const { fullTermPriceBeforeTax, monthlyFullPrice, fullTermDisplayPrice } =
    cycleDetails;
  if (isNoVatInCheckoutSummeryEnabled && fullTermPriceBeforeTax) {
    const monthlyPriceBefore = calculateMonthlyPriceBefore(
      fullTermPriceBeforeTax,
      cycleMultiplier,
    );
    return {
      cycleMonthlyPrice: monthlyPriceBefore,
      cycleFullTermPrice: fullTermPriceBeforeTax,
    };
  }
  return {
    cycleMonthlyPrice: monthlyFullPrice,
    cycleFullTermPrice: fullTermDisplayPrice,
  };
}

function summaryRenderItems({
  currentProduct,
  currentCycle,
  alternativeCycle,
  setCurrentCycle,
  isInSale,
  isNoVatInCheckoutSummeryEnabled,
  isRemoveStrikePriceEnabled,
  isCheckoutSavingsEnabled,
  currencyToString,
  tFunction,
  SummaryRowComp,
  logError,
}: SummaryRenderItemsOptions): SummaryProps['renderItems'] {
  return (items: SummaryItem[]) => {
    changeCycleIfRequired({
      items,
      alternativeCycle,
      currentCycle,
      logError,
      setCurrentCycle,
    });
    const cycleMultiplier = CycleMultiplier[currentCycle];
    const productCycle = currentProductCycle(currentProduct, currentCycle);
    const { cycleMonthlyPrice, cycleFullTermPrice } = getSummeryPrices(
      isNoVatInCheckoutSummeryEnabled,
      productCycle,
      cycleMultiplier,
    );
    const { monthlyFullPrice } = currentProductCycle(
      currentProduct,
      PaymentCycle.MONTHLY,
    );
    const monthlyFullPriceText = currencyToString(monthlyFullPrice);
    const discountSavingsText = currencyToString(
      productCycle.fullTermDisplaySavings,
    );
    const showDiscountSavings =
      !isInSale &&
      currentCycle !== PaymentCycle.MONTHLY &&
      productCycle.fullTermDisplaySavings > 0 &&
      isCheckoutSavingsEnabled;
    const showStrikePrice =
      showDiscountSavings &&
      !!monthlyFullPrice &&
      monthlyFullPrice > cycleMonthlyPrice &&
      !isRemoveStrikePriceEnabled;

    return [
      <div key="product">
        <Text weight="bold" dataHook="test">
          {currentProduct.productName}
        </Text>
        <SummaryRowComp
          labelDataHook="product_cycle_lable"
          valueDataHook="product_cycle_value"
          key="checkout-summary-row"
          value={currencyToString(cycleFullTermPrice)}
          label={
            <CheckoutRowLabel
              currentCycle={currentCycle}
              cycleMonthlyPrice={cycleMonthlyPrice}
              showStrikePrice={showStrikePrice}
              showDiscountSavings={showDiscountSavings}
              discountSavingsText={discountSavingsText}
              monthlyFullPriceText={monthlyFullPriceText}
              t={tFunction}
              currencyToString={currencyToString}
            />
          }
        />
      </div>,
    ];
  };
}

export function getSaleDiscountPercentage(): number {
  return FALLBACK_DISCOUNT_PERCENTAGE;
}

function summaryCouponLabel({
  isInSale,
  isEditorXSpecialDiscountEnabled,
  tFunction,
}: SummaryCouponLabelOptions) {
  return (couponName?: string) => {
    if (couponName && couponNamesToTranslationKeys[couponName]) {
      return tFunction(couponNamesToTranslationKeys[couponName]);
    }
    if (isEditorXSpecialDiscountEnabled) {
      return tFunction(couponNamesToTranslationKeys.Wix2XMigration40);
    }
    if (isInSale) {
      const discountPercent = getSaleDiscountPercentage();
      return tFunction(couponNamesToTranslationKeys.salePercentage, {
        percent: discountPercent,
      });
    }
    return '';
  };
}

export function useSummaryProps(options: UseSummaryPropsOptions): SummaryProps {
  const { state } = useCheckoutStore();
  const {
    tFunction,
    currencyToString,
    renderItemsOptions,
    injectedCouponDisplayNameOptions,
    renderCustomFooterOptions,
  } = options;
  return {
    allowManualCoupon: true,
    renderItems: summaryRenderItems({
      ...renderItemsOptions,
      tFunction,
      currencyToString,
      alternativeCycle: state.alternativeCycle,
    }),
    renderCustomDisclaimer: () => options.customDisclaimer,
    renderCreditExplanation: () => options.creditExplanation,
    renderCustomFooter: () =>
      renderCustomFooterOptions && state.showCustomFooter ? (
        <CheckoutSummaryFooter
          isSecondUpgrade={renderCustomFooterOptions.isSecondUpgrade}
          isChangePlan={renderCustomFooterOptions.isChangePlan}
          t={tFunction}
        />
      ) : null,
    injectedCouponDisplayName: summaryCouponLabel({
      ...injectedCouponDisplayNameOptions,
      tFunction,
    }),
  };
}
